import { Dispatch } from "redux";
import { Product } from "../types/productTypes";
import {
  collection,
  query,
  getDocs,
  getDoc,
  where,
  addDoc,
  orderBy,
} from "firebase/firestore";
import { db } from "../../firebase";
import { ReportedProduct } from "../types/reportedProductsType";
import { sendEmail } from "../../emailHandler";
import { get } from "http";
import { getuid } from "process";
import {
  convertMinutesToHours,
  getDifferenceInMinutesBetweenTimeStamps,
  getHumanReadableTimeDifference,
} from "../../utils";

export const FETCH_REPORTED_PRODUCTS_REQUEST =
  "FETCH_REPORTED_PRODUCTS_REQUEST";
export const FETCH_REPORTED_PRODUCTS_SUCCESS =
  "FETCH_REPORTED_PRODUCTS_SUCCESS";
export const FETCH_REPORTED_PRODUCTS_FAILURE =
  "FETCH_REPORTED_PRODUCTS_FAILURE";
export const FETCH_REPORTED_PRODUCTS_RESET = "FETCH_REPORTED_PRODUCTS_RESET";

export const PAGE_SIZE = 18;

export const fetchReportedProductsRequest = () => ({
  type: FETCH_REPORTED_PRODUCTS_REQUEST,
});

export const fetchReportedProductsSuccess = (reportedProductsPageData: {
  //products: Product[];
  reportedProducts: ReportedProduct[];
  totalRecords?: number;
  success?: boolean;
}) => ({
  type: FETCH_REPORTED_PRODUCTS_SUCCESS,
  payload: reportedProductsPageData,
});

export const fetchReportedProductsFailure = (error: string) => ({
  type: FETCH_REPORTED_PRODUCTS_FAILURE,
  payload: error,
});

export const createReportedProduct =
  (userId: number, productId: number, productURL?: string, username?: string) =>
  async (dispatch: Dispatch) => {
    dispatch(fetchReportedProductsRequest());
    try {
      const existingReportedProduct = await getUniqueReportedProductByIDs(
        userId,
        productId
      );
      if (existingReportedProduct && existingReportedProduct.length) {
        throw new Error("Reported product already exists");
      }

      await validateLimitOnReportedProduct(userId);

      // Add a new document with a generated id
      const docRef = await addDoc(collection(db, "reported_products"), {
        userId,
        productId,
        createdTime: Date.now(),
      });

      // Send email of reported product
      if (productURL) {
        const emailText = username
          ? `${productURL}\n\nReported by: ${username}`
          : productURL;

        await sendEmail(
          "noreply@fiddlepiddle.com",
          "Product Listing Reported",
          emailText,
          "reportProduct"
        );
      }

      // Retrieve the document
      const docSnap = await getDoc(docRef);
      const reportedProducts = [docSnap.data()] as ReportedProduct[];
      dispatch(
        fetchReportedProductsSuccess({ reportedProducts, success: true })
      );
    } catch (error) {
      console.error(error);
      if (error instanceof Error) {
        dispatch(fetchReportedProductsFailure(`${error.message}`));
      } else {
        dispatch(
          fetchReportedProductsFailure(`Error creating items. ${error}`)
        );
      }
    }
  };

export const fetchReportedProductByUniqueId =
  (userId: number, productId: number) => async (dispatch: Dispatch) => {
    dispatch(fetchReportedProductsRequest());
    try {
      const reportedProductPageData: {
        //products: Product[];
        reportedProducts: ReportedProduct[];
        totalRecords?: number;
      } = {
        // products: [],
        reportedProducts: [],
        totalRecords: 0,
      };
      const reportedProduct = await getUniqueReportedProductByIDs(
        userId,
        productId
      );
      reportedProductPageData.reportedProducts = reportedProduct;

      dispatch(fetchReportedProductsSuccess(reportedProductPageData));
    } catch (error) {
      console.error(error);
      dispatch(fetchReportedProductsFailure(`Error fetching items. ${error}`));
    }
  };

const getUniqueReportedProductByIDs = async (
  userId: number,
  productId: number
) => {
  try {
    const reportedProducts: ReportedProduct[] = [];

    /* Query the "users" collection for a document with the specified productUrl. */
    let q = query(
      collection(db, "reported_products"),
      where("userId", "==", userId),
      where("productId", "==", productId)
    );

    let docData: ReportedProduct | undefined;
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      docData = doc.data() as ReportedProduct;
      docData.documentId = doc.id;
      reportedProducts.push(docData);
    });
    return reportedProducts;
  } catch (error) {
    console.error(error);
    throw new Error("Error fetching users.");
  }
};

const validateLimitOnReportedProduct = async (userId: number) => {
  const reportedProducts: ReportedProduct[] = [];

  const q = query(
    collection(db, "reported_products"),
    where("userId", "==", userId),
    orderBy("createdTime", "desc")
  );
  let docData: ReportedProduct | undefined;
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((doc) => {
    docData = doc.data() as ReportedProduct;
    docData.documentId = doc.id;
    reportedProducts.push(docData);
  });
  if (reportedProducts.length >= 3) {
    const latestReportedProduct = reportedProducts[0];
    const minutesAgo = getDifferenceInMinutesBetweenTimeStamps(
      Date.now(),
      latestReportedProduct.createdTime
    );
    const hours = convertMinutesToHours(minutesAgo);
    if (hours <= 24) {
      throw new Error(`Please try again in ${24 - hours} hours`);
    }
  }
};
